import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  FolderOpen, Euro, Banknote, Clock, CheckCircle2,
  TrendingUp, FileText, AlertTriangle, ArrowRight, BarChart3, Bell, Upload, StickyNote
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { getCurrentUser, getUserPaymentDelays } from "@/pages/Login";
import { getRegies, formatCurrency } from "@/utils/simulateurUtils";

const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;

const STATUS_LABELS = {
  rdv_admin: "RDV Admin", en_cours: "En cours", valide: "Validé", termine: "Terminé",
};
const STATUS_COLORS = {
  rdv_admin: "bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/30",
  en_cours: "bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-500/30",
  valide: "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  termine: "bg-muted/50 text-gray-700 border-gray-200",
};
const PROG_LABELS = { "174": "174", mpr: "MPR", cumule: "Op. Cumulée" };
const PROG_COLORS = { "174": "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400", mpr: "bg-blue-500/20 text-blue-600 dark:text-blue-400", cumule: "bg-violet-500/20 text-violet-600 dark:text-violet-400" };

function StatCard({ icon: Icon, label, value, sub, color, onClick }) {
  return (
    <div onClick={onClick} className={cn("border border-border rounded-2xl bg-card p-5 transition-all hover:shadow-lg", onClick && "cursor-pointer hover:-translate-y-0.5")} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", color || "bg-muted")}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
      </div>
      <p className="text-2xl font-extrabold tracking-tight">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

export default function DashboardRegie() {
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState([]);
  const [etudes, setEtudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState({ notifications: [], unread_count: 0 });
  const source = getCurrentUser();
  const delays = getUserPaymentDelays();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dosRes, etuRes, mainDosRes, notifsRes] = await Promise.all([
          fetch(`${API}/etudes?source=${encodeURIComponent(source)}&doc_type=dossier`).then(r => r.json()),
          fetch(`${API}/etudes?source=${encodeURIComponent(source)}&saved_only=true`).then(r => r.json()),
          fetch(`${API}/dossiers`).then(r => r.json()),
          fetch(`${API}/notifications?limit=10&user=${encodeURIComponent(source)}&role=${encodeURIComponent(localStorage.getItem("label_energie_role") || "regie")}`).then(r => r.json()),
        ]);
        // Merge: etude dossiers + main dossiers assigned to current user (avoid duplicates)
        const etudeIds = new Set(dosRes.map(d => d.id));
        const myMainDossiers = mainDosRes
          .filter(d => (d.assigned_to === source || d.created_by === source) && !etudeIds.has(d.id))
          .map(d => ({
            id: d.id, type: "dossier", created_at: d.created_at,
            beneficiaire: { nom: d.client_name || "" },
            recommendation: d.programme || "", status: d.statut_dossier || "",
            surface: d.metrage || 80, precarite: d.precarite || "BLEU",
            regies: null, // will use fallback
            steps: d.steps || [],
          }));
        setDossiers([...dosRes, ...myMainDossiers]);
        setEtudes(etuRes);
        setNotifications(notifsRes || { notifications: [], unread_count: 0 });
      } catch (err) {}
      setLoading(false);
    };
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [source]);

  const stats = useMemo(() => {
    const byStatus = {};
    const byProg = {};
    let totalCommission = 0;
    let encaisse = 0;
    let aVenir = 0;

    dossiers.forEach(d => {
      const st = d.statut_dossier || d.status || "rdv_admin";
      byStatus[st] = (byStatus[st] || 0) + 1;
      const prog = (d.recommendation || d.programme || "174").toLowerCase();
      byProg[prog] = (byProg[prog] || 0) + 1;

      // Calculate commission
      let comm = 0;
      // SCI multi: use user's commission_sci % or default 10%
      if (d.is_sci_multi && (d.quote_amount || d.total_aides)) {
        const ca = d.quote_amount || d.total_aides || 0;
        const userCommStr = localStorage.getItem("label_energie_commissions");
        let sciPct = 10;
        if (userCommStr) { try { sciPct = JSON.parse(userCommStr).commission_sci || 10; } catch (err) {} }
        comm = Math.round(ca / 1.055 * sciPct / 100);
      } else {
        let regies = d.regies;
        if (!regies || Object.keys(regies).length === 0) {
          const surf = d.surface || d.metrage || 80;
          const prec = (d.precarite || "bleu").toLowerCase();
          regies = getRegies("regie", prec === "jaune" ? "jaune" : "bleu", surf);
        }
        if (prog === "cumule" || prog === "cumulée") comm = (regies.r174Cum || 0) + (regies.rMPRCum || 0);
        else if (prog === "174") comm = regies.r174 || 0;
        else if (prog === "mpr") comm = regies.rMPR || 0;
      }
      totalCommission += comm;

      // Check if payment step is done → encaissé, otherwise → à venir
      const steps = d.steps || [];
      const paymentDone = steps.some(s => 
        (s.key === "paye" || s.key === "prime_cee") && s.status === "done"
      );
      if (paymentDone) {
        encaisse += comm;
      } else if (comm > 0) {
        aVenir += comm;
      }
    });

    return { byStatus, byProg, totalCommission, encaisse, aVenir, totalDossiers: dossiers.length, totalEtudes: etudes.length };
  }, [dossiers, etudes]);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-muted-foreground">Chargement du tableau de bord...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-6xl animate-fade-in" data-testid="dashboard-regie">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">
          Bonjour {source}
        </h1>
        <p className="text-muted-foreground mt-1">Tableau de bord</p>
      </div>

      {/* Main stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={FolderOpen} label="Dossiers" value={stats.totalDossiers} sub={`${stats.totalEtudes} études au total`} color="bg-orange-500" />
        <StatCard icon={Euro} label="Commission totale" value={formatCurrency(stats.totalCommission)} sub={`Sur ${stats.totalDossiers} dossiers`} color="bg-[hsl(200,28%,33%)]" />
        <StatCard icon={Banknote} label="Encaissé" value={formatCurrency(stats.encaisse)} sub="Dossiers validés/terminés" color="bg-emerald-500" />
        <StatCard icon={Clock} label="À venir" value={formatCurrency(stats.aVenir)} sub="En attente de validation" color="bg-amber-500" />
      </div>

      {/* Status breakdown + Programme breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Status */}
        <div className="border border-border rounded-2xl bg-card p-5" data-testid="status-breakdown">
          <h3 className="font-semibold text-sm flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-orange-500" /> Répartition par statut
          </h3>
          <div className="space-y-2">
            {Object.entries(STATUS_LABELS).map(([key, label]) => {
              const count = stats.byStatus[key] || 0;
              const pct = stats.totalDossiers > 0 ? (count / stats.totalDossiers) * 100 : 0;
              return (
                <div key={key} className="flex items-center gap-3">
                  <span className={cn("px-2 py-0.5 rounded-md text-[10px] font-bold min-w-[80px] text-center border", STATUS_COLORS[key])}>{label}</span>
                  <div className="flex-1 bg-muted rounded-full h-2.5 overflow-hidden">
                    <div className={cn("h-full rounded-full transition-all", key === "rdv_admin" ? "bg-blue-400" : key === "en_cours" ? "bg-amber-400" : key === "valide" ? "bg-emerald-400" : "bg-gray-400")} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs font-bold w-8 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Programme */}
        <div className="border border-border rounded-2xl bg-card p-5" data-testid="prog-breakdown">
          <h3 className="font-semibold text-sm flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-orange-500" /> Répartition par programme
          </h3>
          <div className="space-y-2">
            {Object.entries(PROG_LABELS).map(([key, label]) => {
              const count = stats.byProg[key] || 0;
              return (
                <div key={key} className="flex items-center gap-3">
                  <span className={cn("px-2 py-0.5 rounded-md text-[10px] font-bold min-w-[80px] text-center", PROG_COLORS[key])}>{label}</span>
                  <div className="flex-1 bg-muted rounded-full h-2.5 overflow-hidden">
                    <div className={cn("h-full rounded-full", key === "174" ? "bg-emerald-400" : key === "mpr" ? "bg-blue-400" : "bg-violet-400")} style={{ width: `${stats.totalDossiers > 0 ? (count / stats.totalDossiers) * 100 : 0}%` }} />
                  </div>
                  <span className="text-xs font-bold w-8 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className="border border-border rounded-2xl bg-card overflow-hidden" data-testid="regie-notifications">
        <div className="p-5 border-b border-border flex items-center justify-between bg-emerald-500/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <Bell className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <h2 className="font-semibold text-base">Notifications</h2>
              <p className="text-xs text-muted-foreground">Activité récente</p>
            </div>
          </div>
          {(notifications?.unread_count || 0) > 0 && (
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-1 rounded-full bg-emerald-500 text-white text-xs font-bold">{notifications.unread_count}</span>
              <Button variant="outline" size="sm" onClick={async () => {
                await fetch(`${API}/notifications/read-all?user=${encodeURIComponent(source)}&role=${encodeURIComponent(localStorage.getItem("label_energie_role") || "regie")}`, { method: "PUT" });
                setNotifications(prev => ({ ...prev, unread_count: 0, notifications: (prev.notifications || []).map(n => ({ ...n, read: true })) }));
              }} className="rounded-lg text-xs">Tout lire</Button>
            </div>
          )}
        </div>
        <div className="divide-y divide-border">
          {(notifications?.notifications || []).length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">Aucune notification</div>
          ) : (notifications.notifications || []).slice(0, 8).map(n => (
            <div key={n.id} className={cn("p-4 flex items-start gap-3 transition-colors cursor-pointer hover:bg-muted/30", !n.read && "bg-emerald-500/5")}
              onClick={() => n.dossier_id && navigate(`/dossier-regie/${n.dossier_id}`)}>
              <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0",
                n.type === "document_uploaded" ? "bg-blue-500/20" : n.type === "dossier_created" ? "bg-indigo-500/20" : "bg-amber-500/20"
              )}>
                {n.type === "document_uploaded" ? <Upload className="w-4 h-4 text-blue-600" /> :
                 n.type === "dossier_created" ? <FileText className="w-4 h-4 text-indigo-600" /> :
                 <StickyNote className="w-4 h-4 text-amber-600" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold">{n.message}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {n.created_at ? new Date(n.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}
                  {n.created_by && ` — ${n.created_by}`}
                </p>
              </div>
              {n.dossier_id && <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0 mt-1" />}
            </div>
          ))}
        </div>
      </div>

      {/* Recent dossiers */}
      <div className="border border-border rounded-2xl bg-card p-5" data-testid="recent-dossiers">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-sm flex items-center gap-2">
            <FileText className="w-4 h-4 text-orange-500" /> Derniers dossiers
          </h3>
          <button onClick={() => navigate("/dossiers-regie")} className="text-xs text-orange-600 hover:text-orange-700 font-semibold flex items-center gap-1">
            Voir tout <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        {dossiers.length === 0 ? (
          <div className="text-center py-8">
            <AlertTriangle className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Aucun dossier pour le moment</p>
            <p className="text-xs text-muted-foreground mt-1">Créez un dossier depuis le simulateur travaux</p>
          </div>
        ) : (
          <div className="space-y-2">
            {dossiers.slice(0, 5).map(d => {
              const regies = d.regies || {};
              const prog = d.recommendation || "174";
              let comm = 0;
              if (prog === "cumule") comm = (regies.r174Cum || 0) + (regies.rMPRCum || 0);
              else if (prog === "174") comm = regies.r174 || 0;
              else comm = regies.rMPR || 0;
              return (
                <div key={d.id} onClick={() => navigate(`/dossier-regie/${d.id}`)}
                  className="flex items-center gap-3 p-3 rounded-xl border border-border hover:bg-muted/30 cursor-pointer transition-colors"
                  data-testid={`recent-dossier-${d.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{d.beneficiaire?.nom || "N/A"}</p>
                    <p className="text-[10px] text-muted-foreground">#{d.id?.slice(-6)} — {d.created_at ? new Date(d.created_at).toLocaleDateString("fr-FR") : "-"}</p>
                  </div>
                  <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-bold", PROG_COLORS[prog])}>{PROG_LABELS[prog]}</span>
                  <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-bold border", STATUS_COLORS[d.status || "rdv_admin"])}>{STATUS_LABELS[d.status || "rdv_admin"]}</span>
                  <span className="text-sm font-bold text-orange-600 min-w-[70px] text-right">{formatCurrency(comm)}</span>
                  <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
